import { useLocation } from '@solidjs/router';
import { createEffect, createSignal, mergeProps, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { addMyContent, deleteMyContentItemByPermalink, isInMyContent } from '../my-content/my-content-helpers';
import { HeartCviIcon } from '../icons-library/heart-cvi';
import { HeartFullCviIcon } from '../icons-library/heart-full-cvi';
import { linkItemClass } from '../share-and-save/share-and-save';

type MyContentHandlerProps = {
    title?: string;
    type: string;
    permalink?: string;
    onlyIcon?: boolean;
};

const defaultProps = {
    title: '',
    type: '',
    onlyIcon: false,
};

/* The "Save to my content" toggle in the share toolbar: saves the item, or removes it
   if it's already saved. */
export const MyContentHandler = (componentProps: MyContentHandlerProps) => {
    const props = mergeProps(defaultProps, componentProps);
    const { localize, siteInfo, userProfileUrls } = useContext(AppContext);
    const [existsInMyContent, setExistsInMyContent] = createSignal(false);
    const location = useLocation();

    createEffect(() => setExistsInMyContent(isInMyContent(props.permalink)));

    const featureEnabled = () => siteInfo.siteType === 'default';
    const isOnBookmarksPage = () => location.pathname === userProfileUrls.bookmarks;

    const handleOnClickSaveToMyContent = (e: any) => {
        e.preventDefault();

        if (existsInMyContent()) {
            deleteMyContentItemByPermalink(props.permalink);
            setExistsInMyContent(false);
            return;
        }

        addMyContent({ title: props.title, permalink: props.permalink, type: props.type });
        setExistsInMyContent(true);
    };

    return (
        <Show when={featureEnabled() && !isOnBookmarksPage()}>
            <button class={linkItemClass} onClick={handleOnClickSaveToMyContent} aria-label={localize('save-to-my-content', 'Save to my content')}>
                <Show
                    when={existsInMyContent()}
                    fallback={
                        <HeartCviIcon size="lg" ariaLabel={localize('my_content_star_icon', 'Add to My Content')} />
                    }
                >
                    <HeartFullCviIcon size="lg" ariaLabel={localize('my_content_remove_star', 'Remove from My Content')} />
                </Show>
                <Show when={props.type === 'page' || !props.onlyIcon}>
                    <span class="body-md font-cvi-regular">
                        {localize('save-to-my-content', 'Save to my content')}
                    </span>
                </Show>
            </button>
        </Show>
    );
};
